import {
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  SortingState,
  TableOptions,
  TableState,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";

interface UseDataTableProps<TData> extends Omit<
  TableOptions<TData>,
  | "state"
  | "getCoreRowModel"
  | "manualPagination"
  | "manualSorting"
  | "enableRowSelection"
> {
  initialState?: Partial<TableState>;
  manualSorting?: boolean;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  state?: Partial<TableState>;
}

export function useDataTable<TData>({
  initialState,
  manualSorting,
  enableRowSelection,
  state,
  ...props
}: UseDataTableProps<TData>) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialState?.pagination?.pageIndex || 0,
    pageSize: initialState?.pagination?.pageSize || 10,
  });

  const [globalFilter, setGlobalFilter] = useState<string>("");

  const [sorting, setSorting] = useState<SortingState>(
    initialState?.sorting || [],
  );

  const [filters, setFilters] = useState<ColumnFiltersState>(
    initialState?.columnFilters || [],
  );

  const table = useReactTable({
    ...props,
    initialState,
    state: {
      pagination,
      sorting,
      globalFilter,
      columnFilters: filters,
      rowSelection: state?.rowSelection || {},
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: manualSorting ?? true,
    enableRowSelection: enableRowSelection ?? true,
  });

  return { table, pagination, sorting, globalFilter, filters };
}
